import {
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  Container,
  Chip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import { fCurrency } from '@/utils/formatNumber';
import { useSummary, useTicketsSelected, usePromotions } from '@/store';
import useEventContext from '@/contexts/event/useEventContext';
import useTicketsContext from '@/contexts/tickets/useTicketsContext';
import { useCheckoutGenerateOrder } from '@/hooks/checkout';
import useMobile from '@/hooks/useMobile';
import Iconify from '@/components/Iconify';
import useRouterTransition from '@/hooks/useRouterTransition';
import useAnalyticsEvents from '@/hooks/useAnalyticsEvents';
import userTracking from '@/hooks/userTracking/index';
import useBoletiaConfig from '@/hooks/useBoletiaConfig';

const ExpandMore = styled((props) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  width: '18px',
  height: '18px',
  padding: '0',
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

const BoxStyle = styled(Box)(({ theme }) => ({
  transition: theme.transitions.create(['height'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter,
  }),
}));

export const FOOTER_DESKTOP_HEIGHT = 77;
export const FOOTER_MOBILE_HEIGHT = 77;
const FOOTER_SUMMARY_HEIGHT = 140;

function FooterSummary() {
  const { t } = useTranslation();
  const { eventUseNewCommision } = useBoletiaConfig();
  const { showCartDetails } = useAnalyticsEvents();
  const { buyTicketsClicked } = userTracking();
  const {
    summary: {
      total_to_pay,
      discount,
      commissions,
      total_tickets,
      total_processor_fee,
      total_transaction_fee,
      total_service_fee,
    },
  } = useSummary();
  const {
    event: {
      event: { id: eventId },
      preferences: { currency, fee_breakdown },
    },
  } = useEventContext();
  const { count } = useTicketsSelected();
  const { generateOrder, isLoading } = useCheckoutGenerateOrder();
  const onBuyTicketsHandler = () => {
    buyTicketsClicked();
    generateOrder();
  };
  const { isMobile } = useMobile();
  const { promotions: promotion, clearPromotion } = usePromotions();
  const [expanded, setExpanded] = useState(false);
  const { isInTransition } = useRouterTransition();

  const expandHandler = () => {
    setExpanded((prev) => !prev);
  };

  const onRemovePromotion = () => {
    clearPromotion();
  };

  const getHeight = () => {
    let footerHeight = isMobile ? FOOTER_MOBILE_HEIGHT : FOOTER_DESKTOP_HEIGHT;
    if (expanded) {
      footerHeight += FOOTER_SUMMARY_HEIGHT;
      if (promotion) {
        footerHeight += 30;
      }
    }
    return `${footerHeight}px`;
  };

  useEffect(() => {
    if (expanded) {
      showCartDetails();
    }
  }, [expanded]);

  return (
    <BoxStyle
      sx={{
        height: getHeight(),
        background: (theme) => theme.palette.background.default,
        position: 'relative',
      }}
    >
      {expanded && (
        <Container sx={{ pb: 2, mt: 1 }}>
          <Stack direction="row" sx={{ width: '100%' }}>
            <Box sx={{ minWidth: 80 }}>
              <Typography
                variant="body3"
                sx={{
                  lineHeight: '36px',
                }}
              >
                {t('checkout.global.subtotal')}
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }}></Box>
            <Box>
              <Typography
                variant="body2"
                sx={{
                  lineHeight: '36px',
                }}
              >
                {`${fCurrency(total_tickets)} ${currency}`}
              </Typography>
            </Box>
          </Stack>
          {promotion && discount > 0 && (
            <Stack direction="row" sx={{ width: '100%' }}>
              <Box sx={{ minWidth: 80 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    lineHeight: '36px',
                  }}
                >
                  <Chip
                    label={promotion.code}
                    size="small"
                    icon={<Iconify width={14} height={14} icon={'mdi:tag'} />}
                    color="secondary"
                    onDelete={onRemovePromotion ? onRemovePromotion : null}
                  />
                </Typography>
              </Box>
              <Box sx={{ flexGrow: 1 }}></Box>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    lineHeight: '36px',
                    color: (theme) => theme.palette.secondary.main,
                  }}
                >
                  {`- ${fCurrency(discount)} ${currency}`}
                </Typography>
              </Box>
            </Stack>
          )}
          {promotion && discount <= 0 && (
            <Stack direction="row" sx={{ width: '100%' }}>
              <Box sx={{ minWidth: 80 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    lineHeight: '36px',
                  }}
                >
                  <Chip
                    label={promotion.code}
                    size="small"
                    icon={<Iconify width={14} height={14} icon={'mdi:tag'} />}
                    color="secondary"
                    onDelete={onRemovePromotion ? onRemovePromotion : null}
                  />
                </Typography>
              </Box>
              <Box sx={{ flexGrow: 1 }}></Box>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    lineHeight: '36px',
                    color: (theme) => theme.palette.secondary.main,
                  }}
                >
                  {t('checkout.global.noPromo')}
                </Typography>
              </Box>
            </Stack>
          )}
          {eventUseNewCommision(eventId, fee_breakdown) ? (
            <>
              <Stack direction="row" sx={{ width: '100%' }}>
                <Box sx={{ minWidth: 80 }}>
                  <Typography
                    variant="body3"
                    sx={{
                      lineHeight: '36px',
                    }}
                  >
                    {t('checkout.global.charge')}
                  </Typography>
                </Box>
                <Box sx={{ flexGrow: 1 }}></Box>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      lineHeight: '36px',
                    }}
                  >
                    {`${fCurrency(commissions.boletia_commission)} ${currency}`}
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" sx={{ width: '100%' }}>
                <Box sx={{ minWidth: 80 }}>
                  <Typography
                    variant="body3"
                    sx={{
                      lineHeight: '36px',
                    }}
                  >
                    {t('checkout.global.paymentProcessor')}
                  </Typography>
                </Box>
                <Box sx={{ flexGrow: 1 }}></Box>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      lineHeight: '36px',
                    }}
                  >
                    {`${fCurrency(
                      commissions.payment_method_commission,
                    )} ${currency}`}
                  </Typography>
                </Box>
              </Stack>
            </>
          ) : (
            <>
              {fee_breakdown === 't' && (
                <>
                  <Stack direction="row" sx={{ width: '100%' }}>
                    <Box sx={{ minWidth: 80 }}>
                      <Typography
                        variant="body3"
                        sx={{
                          lineHeight: '36px',
                        }}
                      >
                        {t('checkout.global.charge')}
                      </Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1 }}></Box>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          lineHeight: '36px',
                        }}
                      >
                        {`${fCurrency(total_service_fee)} ${currency}`}
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" sx={{ width: '100%' }}>
                    <Box sx={{ minWidth: 80 }}>
                      <Typography
                        variant="body3"
                        sx={{
                          lineHeight: '36px',
                        }}
                      >
                        {t('checkout.global.ticketFee')}
                      </Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1 }}></Box>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          lineHeight: '36px',
                        }}
                      >
                        {`${fCurrency(total_transaction_fee)} ${currency}`}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack direction="row" sx={{ width: '100%' }}>
                    <Box sx={{ minWidth: 80 }}>
                      <Typography
                        variant="body3"
                        sx={{
                          lineHeight: '36px',
                        }}
                      >
                        {t('checkout.global.paymentMethodCharge')}
                      </Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1 }}></Box>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          lineHeight: '36px',
                        }}
                      >
                        {`${fCurrency(total_processor_fee)} ${currency}`}
                      </Typography>
                    </Box>
                  </Stack>
                </>
              )}
            </>
          )}
        </Container>
      )}
      <Stack
        direction="row"
        sx={{
          position: 'absolute',
          bottom: 0,
          background: (theme) => theme.palette.background.default,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          height: isMobile
            ? `${FOOTER_MOBILE_HEIGHT}px`
            : `${FOOTER_DESKTOP_HEIGHT}px`,
        }}
      >
        <Box sx={{ minWidth: 160 }}>
          <Stack direction="row" sx={{ width: '100%', alignItems: 'flex-end' }}>
            <Typography
              variant="title"
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                lineHeight: '16px',
              }}
              component="div"
            >
              {`${fCurrency(total_to_pay)} `}
            </Typography>
            <Typography
              variant="caption3"
              component="div"
              sx={{
                ml: 0.5,
                fontSize: '12px',
                lineHeight: '12px',
                fontWeight: 600,
              }}
            >
              {currency}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            sx={{ width: '100%', alignItems: 'center', mt: 0.5 }}
          >
            <Box
              onClick={expandHandler}
              sx={{
                '&:hover': {
                  cursor: 'pointer',
                },
              }}
            >
              <Typography
                variant="caption3"
                component="div"
                sx={{
                  fontSize: '10px',
                  lineHeight: '10px',
                  textDecoration: 'underline',
                }}
              >
                {t('checkout.global.ReviewPurchase')}
              </Typography>
            </Box>

            <ExpandMore expand={expanded} onClick={expandHandler}>
              <Iconify
                width={14}
                height={14}
                icon={'material-symbols:keyboard-arrow-up'}
              />
            </ExpandMore>
          </Stack>
        </Box>
        <Box sx={{ flexGrow: 1 }}></Box>
        <Box width={'60%'}>
          <Button
            variant="contained"
            sx={{ height: '45px', width: '100%' }}
            onClick={onBuyTicketsHandler}
            disabled={isInTransition || isLoading}
          >
            {t('checkout.buttons.buyTickets', { count })}
          </Button>
        </Box>
      </Stack>
    </BoxStyle>
  );
}

export default FooterSummary;
